import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	inject,
	Input,
	Output,
	ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder } from "@angular/forms";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => NumberInputComponent),
	multi: true,
};

@Component({
	selector: "ic-number-input",
	styleUrls: ["input.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./input.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class NumberInputComponent implements ControlValueAccessor {
	private readonly cdRef = inject(ChangeDetectorRef);
	private readonly formBuilder = inject(UntypedFormBuilder);

	form = this.formBuilder.group({
		value: [""],
	});

	@Input() isPending: boolean;
	@Input() label: string;
	@Input() placeholder = "";
	@Input() icon: string;
	@Input() inputClass = "";
	@Input() disabled = false;
	@Input() required = true;
	@Input() help: string;
	@Input() suffix: string;
	@Input() min: number | string;
	@Input() max: number | string;
	@Input() distinguishNonNumericFromEmpty: boolean;

	@Output() blurEvent = new EventEmitter();

	@ViewChild("input", { static: true }) input: ElementRef;

	pendingIcon = "fa fa-spin fa-refresh";

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	writeValue(value: number) {
		if (value !== 0 && !value) {
			this.form.reset();
			this.cdRef.markForCheck();

			return;
		}

		this.form.patchValue({ value });
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		this.cdRef.markForCheck();
	}

	onChange() {
		this.onModelChange(this.form.getRawValue().value);
	}

	onTouched() {
		this.blurEvent.emit();
		this.onTouch();
	}

	focus() {
		this.input.nativeElement.focus();
	}
}
